import { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface TooltipProps {
  /**
   * The content to display in the tooltip
   */
  content: ReactNode;
  /**
   * The element that triggers the tooltip
   */
  children: ReactNode;
  /**
   * Placement of the tooltip
   */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Delay before showing tooltip (ms)
   */
  delay?: number;
  /**
   * Whether the tooltip is disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS classes for the tooltip
   */
  className?: string;
  /**
   * Show arrow pointing to trigger element
   */
  arrow?: boolean;
  /**
   * Maximum width of tooltip
   */
  maxWidth?: number;
}

export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 500,
  disabled = false,
  className,
  arrow = true,
  maxWidth = 250,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) {
      setIsPositioned(false);
      return;
    }

    const trigger = triggerRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    const spacing = 8; // Space between trigger and tooltip

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top':
        top = trigger.top - tooltip.height - spacing;
        left = trigger.left + trigger.width / 2 - tooltip.width / 2;
        break;
      case 'bottom':
        top = trigger.bottom + spacing;
        left = trigger.left + trigger.width / 2 - tooltip.width / 2;
        break;
      case 'left':
        top = trigger.top + trigger.height / 2 - tooltip.height / 2;
        left = trigger.left - tooltip.width - spacing;
        break;
      case 'right':
        top = trigger.top + trigger.height / 2 - tooltip.height / 2;
        left = trigger.right + spacing;
        break;
    }

    // Keep tooltip within viewport
    const padding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < padding) left = padding;
    if (left + tooltip.width > viewportWidth - padding) {
      left = viewportWidth - tooltip.width - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltip.height > viewportHeight - padding) {
      top = viewportHeight - tooltip.height - padding;
    }

    setPosition({ top, left });
    setIsPositioned(true);
  }, [isVisible, placement]);

  const handleMouseEnter = () => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsVisible(true);
  };

  const handleBlur = () => {
    setIsVisible(false);
  };

  const arrowClasses = {
    top: 'bottom-[-4px] left-1/2 -translate-x-1/2 border-t-zinc-800 border-x-transparent border-b-transparent',
    bottom:
      'top-[-4px] left-1/2 -translate-x-1/2 border-b-zinc-800 border-x-transparent border-t-transparent',
    left: 'right-[-4px] top-1/2 -translate-y-1/2 border-l-zinc-800 border-y-transparent border-r-transparent',
    right:
      'left-[-4px] top-1/2 -translate-y-1/2 border-r-zinc-800 border-y-transparent border-l-transparent',
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="inline-block"
      >
        {children}
      </div>
      {isVisible &&
        content &&
        createPortal(
          <div
            ref={tooltipRef}
            className={cn(
              'pointer-events-none fixed z-50 rounded-lg bg-zinc-800 px-3 py-2 text-sm text-white shadow-lg dark:bg-zinc-700',
              'transition-opacity duration-100',
              isPositioned ? 'opacity-100' : 'opacity-0',
              className
            )}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              maxWidth: `${maxWidth}px`,
            }}
            role="tooltip"
          >
            {content}
            {arrow && <div className={cn('absolute h-0 w-0 border-4', arrowClasses[placement])} />}
          </div>,
          document.body as HTMLElement
        )}
    </>
  );
}

/**
 * Simple tooltip for icon buttons
 */
interface IconTooltipProps {
  content: string;
  children: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export function IconTooltip({ content, children, placement = 'top' }: IconTooltipProps) {
  return (
    <Tooltip content={content} placement={placement} delay={300} className="text-xs">
      {children}
    </Tooltip>
  );
}

/**
 * Keyboard shortcut tooltip
 */
interface ShortcutTooltipProps {
  label: string;
  shortcut: string;
  children: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export function ShortcutTooltip({
  label,
  shortcut,
  children,
  placement = 'bottom',
}: ShortcutTooltipProps) {
  const content = (
    <div className="flex items-center gap-2">
      <span>{label}</span>
      <kbd className="rounded bg-zinc-700 px-1.5 py-0.5 text-xs dark:bg-zinc-600">{shortcut}</kbd>
    </div>
  );

  return (
    <Tooltip content={content} placement={placement} delay={500}>
      {children}
    </Tooltip>
  );
}
